import { PaginationState } from '@innovyze/lib-am-common';
import { StatusType } from '@innovyze/stylovyze';

export interface GetDeteriorationModelsPaginatedRequest {
	page: number;
	slice: number;
	sortKey: string;
	sortDescending: boolean;
}

export enum DeteriorationModelStatus {
	initializing = 'initializing',
	draft = 'draft',
}

export type ExtendedDeteriorationModelStatus = StatusType | DeteriorationModelStatus;

export const deteriorationModelStatusToStatusType = (
	status: string,
	extended = false,
): ExtendedDeteriorationModelStatus => {
	switch (status) {
		case 'initializing':
			return extended ? DeteriorationModelStatus.initializing : StatusType.inProgress;
		case 'draft':
			return DeteriorationModelStatus.draft;
		case 'in progress':
			return StatusType.inProgress;
		case 'completed':
			return StatusType.complete;
		case 'deleted':
			return StatusType.cancelled;
		default:
			return StatusType.failed;
	}
};

export interface DeteriorationModelCard {
	id: string;
	name: string;
	status: ExtendedDeteriorationModelStatus;
	createdBy: string;
	createdAt: string;
	trainedBy: string;
	trainedAt: string;
	updatedBy: string;
	updatedAt: string;
	runBy: string;
	runAt: string;
	lastRun?: string;
	assetGroupSetId: string;
	failureDefinitionId: string | null;
	published?: boolean;
}

export type FilterFieldValue = { operator: string; value: (string | null)[] | string | null };

export enum WeibullFormulation {
	TWO_PARAMETER = 'weibull',
	THREE_PARAMETER = 'weibull_3p',
}

/**
 * Why the backend did not return a fit for a formulation (I360A-2882). Reported alongside the
 * curves so the 2-parameter fallback can be explained: `NO_MINIMUM_LIFE` is a property of the
 * data and not a failure at all, whereas `LOCATION_ON_BOUNDARY` usually means the failure dates
 * are placeholders. Absent for groups trained before that change — treat a missing reason as
 * unspecified and say nothing more than we do today.
 *
 * This types the *wire* fields (`declines`, `deterioration_params.fallback_reason`), so it is a
 * description of what the backend sends today, not a guarantee. The backend can add a sixth
 * reason without a frontend release. Always consume through a function with an explicit
 * `default` — never a non-`Partial` `Record` keyed on this, and never a `default`-less switch,
 * either of which turns a new backend value into a blank message or a crash.
 */
export enum WeibullDeclineReason {
	INSUFFICIENT_FAILURES = 'insufficient_failures',
	NO_MINIMUM_LIFE = 'no_minimum_life',
	LOCATION_ON_BOUNDARY = 'location_on_boundary',
	IMPLAUSIBLE_SHAPE = 'implausible_shape',
	FIT_FAILED = 'fit_failed',
}

export interface DeteriorationModel {
	id: string;
	tenantId: string;
	name: string;
	assetGroupSetId: string;
	failureDefinitionId: string | null;
	status: ExtendedDeteriorationModelStatus;
	createdBy: string;
	createdAt: string;
	trainedBy: string;
	trainedAt: string | null;
	latestTrainResultsId: string | null;
	updatedBy: string;
	updatedAt: string;
	latestCurveConfigsId: string | null;
	runBy: string | null;
	runAt: string | null;
	latestRunResultsId: string | null;
	published?: boolean;
	failureGradeConfigId?: string;
	weibullFormulation?: WeibullFormulation;
	/** Copied from the model's asset group set when the model is created. Absent on older models. */
	systemType?: string;
	assetType?: string;
}

export interface GetDeteriorationModelCurveConfigsPaginatedRequest {
	modelId: string;
	page: number;
	slice: number;
	sortKey: string;
	sortDescending: boolean;
	searchValue: string | null;
	filter: {
		assetGroupName?: FilterFieldValue;
		assetGroupDescription?: FilterFieldValue;
		selectedCurveMethod?: FilterFieldValue;
		assetCount?: FilterFieldValue;
		failureCount?: FilterFieldValue;
	} | null;
	filterModel: {
		items: Array<{
			id?: string | number;
			field: string;
			operator: string;
			value?: string | string[] | null;
		}>;
		logicOperator?: string;
	} | null;
}

export interface CurveConfig {
	curveConfigId: string;
	selectedCurveMethod: CurveMethod;
	reference: string | null;
	weibullFormulation?: WeibullFormulation;
	data:
		| {
				age: number;
				survivalProbability: number;
		  }[]
		| number
		| null;
}

export interface InferByIndustryStandardCurveResponse {
	data: Record<string, { age: number; survival_probability: number }[]>;
}

export enum CurveMethod {
	UNDEFINED = 'undefined',
	FAILURE_DEFINITION = 'failureDefinition',
	CUSTOM_CURVE = 'customCurve',
	INDUSTRY_CURVE = 'industryCurve',
	COPIED_CURVE = 'copiedCurve',
	ONE_EXPECTED_LIFE = 'oneExpectedLife',
}

export const mapCurveMethodResponseToCurveMethod = (method: string | null): CurveMethod => {
	switch (method) {
		case 'undefined':
			return CurveMethod.UNDEFINED;
		case 'failure_definition':
			return CurveMethod.FAILURE_DEFINITION;
		case 'custom_curve':
			return CurveMethod.CUSTOM_CURVE;
		case 'industry_curve':
			return CurveMethod.INDUSTRY_CURVE;
		case 'copied_curve':
			return CurveMethod.COPIED_CURVE;
		case 'one_expected_life':
			return CurveMethod.ONE_EXPECTED_LIFE;
		default:
			return CurveMethod.UNDEFINED;
	}
};

export interface GetDeteriorationModelAssetsResultsPaginatedRequests {
	modelId: string;
	runId: string;
	page: number;
	slice: number;
	sortKey: string;
	sortDescending: boolean;
	searchValue: string | null;
	filter: {
		assetId?: FilterFieldValue;
		assetGroupName?: FilterFieldValue;
		failureProbability?: FilterFieldValue;
		age?: FilterFieldValue;
		rul?: FilterFieldValue;
		failureYear?: FilterFieldValue;
		historicalFailure?: FilterFieldValue;
		grade?: FilterFieldValue;
		material?: FilterFieldValue;
		diameter?: FilterFieldValue;
		installationYear?: FilterFieldValue;
	} | null;
	filterModel: {
		items: Array<{
			id?: string | number;
			field: string;
			operator: string;
			value?: string | string[] | null;
		}>;
		logicOperator?: string;
	} | null;
}

export interface GetDeteriorationModelGroupsResultsPaginatedRequests {
	modelId: string;
	runId: string;
	page: number;
	slice: number;
	sortKey: string;
	sortDescending: boolean;
	searchValue: string | null;
	filter: {
		assetGroupName?: FilterFieldValue;
		assetGroupDescription?: FilterFieldValue;
		assetCount?: FilterFieldValue;
		failureCount?: FilterFieldValue;
		deteriorationMethod?: FilterFieldValue;
		thresholdAge?: FilterFieldValue;
	} | null;
	filterModel: {
		items: Array<{
			id?: string | number;
			field: string;
			operator: string;
			value?: string | string[] | null;
		}>;
		logicOperator?: string;
	} | null;
}

export interface DeteriorationModelPaginationState {
	configuration?: GetDeteriorationModelCurveConfigsPaginatedRequest;
	results: {
		groups: GetDeteriorationModelGroupsResultsPaginatedRequests;
		assets: GetDeteriorationModelAssetsResultsPaginatedRequests;
	};
}

export interface DeteriorationModelPaginationStore {
	[modelId: string]: DeteriorationModelPaginationState;
}

export interface DeteriorationModelsStoreState {
	deteriorationModelsList: PaginationState;
	selectedDeteriorationModel: {
		inProcess: boolean;
		successful: boolean | null;
		deteriorationModel: DeteriorationModel | null;
	};
	deteriorationModelCurveConfigsList: {
		pagination: PaginationState;
		list: {
			curveConfigId: string;
			selectedCurveMethod: CurveMethod;
			reference: string | null;
			weibullFormulation?: WeibullFormulation;
			data: { age: number; survivalProbability: number }[] | number | null;
		}[];
	};
	industryStandardCurves: {
		isLoading: boolean;
		curves: { displayName: string; key: string }[];
	};
	globalUndefinedCount: {
		count: number;
	};
	assetResults: PaginationState;
	/** The request behind `assetResults`: set when it is sent, cleared if it fails. */
	assetResultsRequest: GetDeteriorationModelAssetsResultsPaginatedRequests | null;
	groupResults: PaginationState;
	paginationState: DeteriorationModelPaginationStore;
	/**
	 * Per model, the grade card whose breakdown is open. Kept in the store, like
	 * paginationState, so it survives the Results tab remounting on the way back
	 * from asset details.
	 */
	selectedBreakdownGrade: { [modelId: string]: string };
}

export interface SingleDeteriorationModelAssetResultResponse {
	asset_id: string;
	asset_group_name: string;
	failure_probability: number | null;
	age: number | null;
	rul: number | null;
	failure_year: number | null;
	historical_failure: string;
	grade: 'negligible' | 'low' | 'medium' | 'high' | 'extreme';
	material: string | null;
	diameter: number | null;
	installation_year: number | null;
}
